import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { EkycMetaService } from 'src/app/admin/assisted-ekyc/shared/ekyc-meta.service';
import { EkycTier } from 'src/app/admin/assisted-ekyc/configuration/configuration.model';

import { ConfigStore } from './mock-configuration.service';
import {
  ACCOUNT_PRODUCTS,
  BRANCHES,
  CARD_PRODUCTS,
  DISTRICTS,
  DIVISIONS,
  EXISTING_ACCOUNTS,
  EXISTING_ACCOUNTS_BY_MOBILE,
  INCOME_SOURCES,
  NOMINEE_RELATIONS,
  OCCUPATION_CATEGORIES,
  PROFESSIONS,
  RELIGIONS,
  SECTORS,
  THANAS,
} from '../fixtures/ekyc-meta.fixture';

const MOCK_LATENCY_MS = 200;

function envelope<T>(data: T) {
  return { responseCode: 'S100000', responseMessage: 'Operation successful', data };
}

function respond<T>(data: T): Observable<any> {
  return of(envelope(data)).pipe(delay(MOCK_LATENCY_MS));
}

/** Reference-data mock for the wizard. */
@Injectable()
export class MockEkycMetaService extends EkycMetaService {
  constructor(http: HttpClient, appConfig: ApplicationConfigService) {
    super(http, appConfig);
  }

  override divisions(): Observable<any> {
    return respond(DIVISIONS);
  }

  override districts(_accountClass: string, divisionId: string): Observable<any> {
    return respond(DISTRICTS.filter((d) => d.divisionId === String(divisionId)));
  }

  override thanas(_accountClass: string, districtId: string): Observable<any> {
    return respond(THANAS.filter((t) => t.districtId === String(districtId)));
  }

  override branches(_accountClass: string, districtId: string): Observable<any> {
    const filtered = BRANCHES.filter((b) => b.districtId === String(districtId));
    return respond(filtered.length ? filtered : BRANCHES);
  }

  override religions(): Observable<any> {
    return respond({ settingsValue: RELIGIONS });
  }

  override professions(): Observable<any> {
    return respond({ settingsValue: PROFESSIONS });
  }

  override occupationCategories(): Observable<any> {
    return respond(OCCUPATION_CATEGORIES);
  }

  override sectors(): Observable<any> {
    return respond(SECTORS);
  }

  override incomeSources(): Observable<any> {
    return respond({ settingsValue: INCOME_SOURCES });
  }

  override nomineeRelations(): Observable<any> {
    return respond({ settingsValue: NOMINEE_RELATIONS });
  }

  /**
   * Products are filtered by account class AND by tier: the Simplified product is the only one
   * offered on the Simplified tier, and vice versa. This is the product-level half of the Q1
   * decision; the slab-level half is transactionProfiles() below.
   */
  override accountProducts(accountClass: string, tier: EkycTier): Observable<any> {
    const simplifiedCodes = ['PRD-SIMPLE', 'PRD-ISL-SIMPLE'];
    const rows = ACCOUNT_PRODUCTS.filter((p) => p.accountClass === accountClass).filter((p) =>
      tier === 'SIMPLIFIED' ? simplifiedCodes.includes(p.productTypeCode) : !simplifiedCodes.includes(p.productTypeCode),
    );
    return respond(rows);
  }

  override cardProducts(accountClass: string): Observable<any> {
    return respond(CARD_PRODUCTS.filter((c) => c.accountClass === accountClass));
  }

  /**
   * The Q1 filter proper — only slabs whose ekycType matches the Step 1 tier are returned, so the
   * mismatch state contracts-1 §6.5 used to block on is unreachable by construction.
   */
  override transactionProfiles(tier: EkycTier): Observable<any> {
    return respond(ConfigStore.profilesForTier(tier));
  }

  /**
   * CBS dedupe. Matches on NID first, then falls back to the mobile — so a customer whose NID is
   * recorded differently in CBS is still found by their number (SRS §M4).
   */
  override existingAccounts(nid: string, mobile: string): Observable<any> {
    const byNid = EXISTING_ACCOUNTS[nid];
    if (byNid?.length) {
      return respond(byNid);
    }
    const byMobile = mobile ? EXISTING_ACCOUNTS_BY_MOBILE[mobile] : undefined;
    return respond(byMobile ?? []);
  }

  // ---- identity verification ------------------------------------------------------------------

  /**
   * OCR. Values are derived from the document number so a demo is repeatable: the same number
   * always produces the same extracted name and date of birth.
   *
   * `identityType` follows the Region chosen on Account Type — PASSPORT for Abroad, NID otherwise.
   */
  override parseNid(side: 'front' | 'back', payload: FormData): Observable<any> {
    const isPassport = (payload.get('identityType') as string) === 'PASSPORT';
    const nid = (payload.get('nid') as string) || (isPassport ? 'A01234567' : '1990123456789');
    const documentType = isPassport ? 'PASSPORT' : 'NID_SMART';
    const seed = Number(nid.slice(-2)) || 42;
    const names = ['Arif Hossain', 'Mahmuda Khatun', 'Rakib Chowdhury', 'Sultana Razia', 'Nasir Uddin'];
    const name = names[seed % names.length];

    const surname = name.split(' ').slice(-1)[0];

    if (side === 'front') {
      return respond({
        documentType,
        identityNumber: nid,
        nationalIdNumber: nid,
        dateOfBirth: `19${80 + (seed % 20)}-0${1 + (seed % 9)}-1${seed % 9}`,
        fullNameEnglish: name,
        fullNameLocal: `${name} (বাংলা)`,
        // Both scripts for both parents, so every Check & Confirm field prefills.
        fathersNameEnglish: `Abdul ${surname}`,
        fathersNameLocal: `আব্দুল ${surname}`,
        mothersNameEnglish: 'Rahima Begum',
        mothersNameLocal: 'রহিমা বেগম',
      });
    }

    return respond({
      documentType,
      identityNumber: nid,
      nationalIdNumber: nid,
      presentAddressLocal: 'House 12, Road 4, Dhanmondi, Dhaka',
      permanentAddressLocal: 'Village Sonapur, Cumilla',
      districtLocal: 'ঢাকা',
      // Ids match the reference-data fixtures so the cascade dropdowns resolve when prefilled.
      permanentAddress: {
        divisionId: '1',
        districtId: '11',
        thanaUpazilaId: '111',
        postOffice: 'Motijheel TSO',
        postCode: '1000',
      },
    });
  }

  /**
   * EC validation. Fails deliberately when the NID ends in `0000`, so the VERIFICATION_FAILED
   * retry path in contracts-2 B2.14 can be demonstrated on demand.
   */
  override validateEcInfo(payload: any): Observable<any> {
    const nid: string = payload?.nid ?? '';
    if (nid.endsWith('0000')) {
      return of({
        responseCode: 'S100000',
        responseMessage: 'Verification failed',
        data: {
          verifiedStatus: false,
          fieldVerificationResult: { nid: false, dateOfBirth: false },
        },
      }).pipe(delay(MOCK_LATENCY_MS));
    }
    return respond({ verifiedStatus: true, fieldVerificationResult: {} });
  }

  /**
   * Face match. Scores below the threshold when the NID ends in `9`, so the manual-review path
   * (contracts-2 B2b.4) is reachable without editing fixtures.
   */
  override faceMatch(payload: any): Observable<any> {
    const nid: string = payload?.nid ?? '';
    const belowThreshold = nid.endsWith('9');
    return respond({
      score: belowThreshold ? 61 : 88 + (Number(nid.slice(-1)) || 0) % 10,
      threshold: 75,
      livenessPassed: true,
    });
  }

  /**
   * Screening. A small mock watchlist keyed on surname decides the outcome, so the Screening Hold
   * path is reachable deterministically: any applicant whose name contains one of these words
   * comes back as a potential match.
   */
  override screen(payload: any): Observable<any> {
    const name: string = (payload?.applicantName ?? '').toUpperCase();
    const watchlist = ['KAMAL', 'IBRAHIM', 'HOLD'];
    const hit = watchlist.some((entry) => name.includes(entry));
    return respond({
      status: hit ? 'POTENTIAL_MATCH' : 'CLEAR',
      screenedAt: new Date().toISOString(),
      reference: `SCR-${Math.floor(100000 + Math.random() * 899999)}`,
      holdReason: hit ? 'Name similarity against the UN consolidated list — under review by AML.' : null,
      pepConfirmed: !!payload?.isPublicFigure,
    });
  }

  /**
   * Risk grading, computed from the live Configuration pages rather than hardcoded.
   *
   * This is what makes the four config pages meaningful: editing a score there changes the grade
   * a NEW application receives, while in-flight applications keep the model version they were
   * graded under (SRS §M7).
   */
  override grade(payload: any): Observable<any> {
    const model = ConfigStore.activeGradingModel();
    const answers: Record<string, string> = payload?.answers ?? {};

    const lines = model
      .filter((grading) => grading.riskGradingScoreType !== 'NONE_SCORE')
      .map((grading) => {
        const selectedCode = answers[grading.code];
        const selected = (grading.riskGradingScores ?? []).find((s) => s.code === selectedCode);
        const score = selected?.score ?? 0;
        const weight = grading.weight ?? 1;
        return {
          riskGradingCode: grading.code,
          description: grading.description,
          riskGradingScoreType: grading.riskGradingScoreType,
          selectedScoreCode: selected?.code ?? null,
          selectedScoreDescription: selected?.description ?? null,
          score,
          weight,
          contribution: score * weight,
        };
      });

    const totalScore = lines.reduce((sum, line) => sum + line.contribution, 0);
    const band = ConfigStore.bandForScore(totalScore);

    return respond({
      totalScore,
      band: band.band,
      modelVersion: ConfigStore.riskModelVersion(),
      lines,
    });
  }

  /** No object store in the showcase — a data URL keeps the preview working after a reload. */
  override uploadFile(payload: FormData): Observable<any> {
    const file = payload.get('file') as File | null;
    if (!file) return respond({ path: null, scanStatus: 'CLEAN' });

    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(envelope({ path: String(reader.result), fileName: file.name, scanStatus: 'CLEAN' }));
        observer.complete();
      };
      reader.onerror = () => observer.error(new Error('Read failed'));
      reader.readAsDataURL(file);
    });
  }

  /** The grading parameters and their options, for the Risk Grading step's answer form. */
  override gradingModel(): Observable<any> {
    return respond(ConfigStore.activeGradingModel());
  }
}
